import { XMarkIcon } from '@heroicons/react/20/solid';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';

import { cn } from './cn';

export interface TagsInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  className?: string;
  /**
   * Keep everything on a single line: chips do not wrap and overflow scrolls horizontally with a hidden scrollbar.
   * Useful in dense layouts like table cells where a growing multi-line height is undesirable.
   */
  singleLine?: boolean;
}

/**
 * Controlled chips editor: type a tag and press Enter (or comma) to add it, click the × or press
 * Backspace on an empty input to remove. New-design equivalent of the legacy `TextListEditor`.
 */
export const TagsInput = ({
  value,
  onChange,
  placeholder = 'Add tag and press Enter',
  disabled,
  id,
  ariaLabel,
  className,
  singleLine,
}: TagsInputProps) => {
  const [draft, setDraft] = React.useState('');
  const generatedId = React.useId();
  const inputId = id || generatedId;

  // Commas are a tag separator everywhere, not just on keydown — pasted text like "a,b,c" and
  // blur-committed drafts must split the same way the ','-key path does.
  const addTag = (raw: string) => {
    const tags = raw
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
    const next = [...value];
    for (const tag of tags) {
      if (!next.includes(tag)) next.push(tag);
    }
    if (next.length !== value.length) onChange(next);
    setDraft('');
  };

  const removeTag = (tag: string) => onChange(value.filter((t) => t !== tag));

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag(draft);
    } else if (event.key === 'Escape') {
      setDraft('');
    } else if (event.key === 'Backspace' && !draft && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1.5 rounded-md border border-gray-300 bg-surface px-2 py-1.5 transition-all duration-200',
        'focus-within:border-info-500 focus-within:ring-2 focus-within:ring-info-500/20',
        disabled && 'bg-gray-50 cursor-not-allowed opacity-60',
        singleLine &&
          'flex-nowrap overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
        className
      )}
      onClick={() => !disabled && document.getElementById(inputId)?.focus()}
    >
      <AnimatePresence initial={false}>
        {value.map((tag) => (
          <motion.span
            key={tag}
            layout
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.15 }}
            className={cn(
              'inline-flex items-center gap-1 rounded-full bg-info-100 py-0.5 pl-2.5 pr-1 type-label text-info-700',
              // On a single line, chips must keep their natural width (no shrinking/character-wrapping) so
              // overflow scrolls horizontally instead of growing the container's height.
              singleLine && 'shrink-0 whitespace-nowrap'
            )}
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                // Keep focus on the input so removing a chip doesn't blur-commit a partially typed draft.
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.stopPropagation();
                  removeTag(tag);
                }}
                className="flex h-4 w-4 items-center justify-center rounded-full border-none bg-transparent text-info-700 hover:bg-info-200 cursor-pointer transition-colors duration-150"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            )}
          </motion.span>
        ))}
      </AnimatePresence>
      <input
        id={inputId}
        type="text"
        disabled={disabled}
        value={draft}
        aria-label={ariaLabel}
        placeholder={value.length === 0 ? placeholder : ''}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => addTag(draft)}
        className="min-w-32 flex-1 border-none bg-transparent px-1 py-0.5 text-sm text-text-primary outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
      />
    </div>
  );
};
